import { useMemo, useState, useCallback, useEffect } from "react";
import { v4 } from "uuid";
import { ArrowLeft, Layers, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { Badge } from "../../../../components/ui/badge";
import { Button } from "../../../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../../components/ui/card";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import {
  useBusinessContext,
  useUpdateBusinessContext,
} from "../../../../hooks/useBusinessContext";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import notify from "../../../../utils/notify";
import type { Segment } from "../../../../types/businessContext";
import SignalFilterContainer from "../../signals/signal-overview/components/SignalFilterContainer";
import useObjectSearchTable from "../../../../shared/table/useObjectSearchTable";
import { cn } from "../../../../lib/utils";

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const COLOR_SWATCHES = [
  "#ef4444", "#f97316", "#f59e0b", "#eab308", "#84cc16", "#22c55e",
  "#10b981", "#06b6d4", "#3b82f6", "#6366f1", "#8b5cf6", "#a855f7",
];

const EMPTY_FILTER = {
  query_type: "AND",
  predicates: [
    { predicates: [{ field: "", operator: "", value: "", type: "", entity_type: "column" }] },
  ],
};

const DEFAULT_PREVIEW_FIELDS = ["accounts.name", "accounts.external_id", "accounts.last_seen"];

/* ------------------------------------------------------------------ */
/*  Color picker (reused from BusinessContextPage pattern)             */
/* ------------------------------------------------------------------ */

const ColorField = ({
  label: fieldLabel,
  value,
  onChange,
}: {
  label?: string;
  value: string;
  onChange: (v: string) => void;
}) => (
  <div className="space-y-1.5">
    {fieldLabel && <Label>{fieldLabel}</Label>}
    <div className="flex items-center gap-2">
      <div className="flex gap-1 flex-wrap">
        {COLOR_SWATCHES.map((c) => (
          <button
            key={c}
            type="button"
            className={cn(
              "h-6 w-6 rounded-full border-2 transition-all",
              value === c ? "border-foreground scale-110" : "border-transparent",
            )}
            style={{ backgroundColor: c }}
            onClick={() => onChange(c)}
          />
        ))}
      </div>
      <Input className="w-24 h-7 text-xs" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Account preview for a filter                                       */
/* ------------------------------------------------------------------ */

const SegmentPreview = ({
  workspaceId,
  filter,
}: {
  workspaceId: string;
  filter: any;
}) => {
  const hasPredicates = filter?.predicates?.some(
    (group: any) => group?.predicates?.some((p: any) => p?.field?.trim()),
  );

  const { rows, total, isLoading, isFetching } = useObjectSearchTable({
    workspaceId,
    entityType: "accounts",
    enabled: !!workspaceId && hasPredicates,
    initialPageSize: 5,
    initialSort: { field: "accounts.last_seen", order: "desc" },
    queryOptions: {
      filter: hasPredicates ? filter : undefined,
      selectedFields: DEFAULT_PREVIEW_FIELDS,
      fallbackSortField: "accounts.last_seen",
    },
    parseRows: (rawRows) =>
      (rawRows || []).map((record: any) => {
        const fields = record?.fields || {};
        return {
          name: fields.name || record?.name || "",
          external_id: fields.external_id || record?.id || "",
          last_seen: fields.last_seen || "",
        };
      }),
    resetPageDeps: [JSON.stringify(filter)],
  });

  if (!hasPredicates) {
    return (
      <p className="text-xs text-muted-foreground italic">
        Add filter conditions above to preview matching accounts.
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading preview...
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Badge
          variant="outline"
          className={cn(
            "rounded-full px-2 text-[11px]",
            total === 0 && "border-amber-300 text-amber-700",
          )}
        >
          {isFetching ? "..." : total} account{total !== 1 ? "s" : ""} match
        </Badge>
        {total === 0 && (
          <span className="text-xs text-amber-600">No accounts match these conditions</span>
        )}
      </div>
      {rows.length > 0 && (
        <div className="overflow-hidden rounded-md border border-border text-xs">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-3 py-1.5 text-left font-medium text-muted-foreground">Name</th>
                <th className="px-3 py-1.5 text-left font-medium text-muted-foreground">External ID</th>
                <th className="px-3 py-1.5 text-left font-medium text-muted-foreground">Last Seen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row: any, idx: number) => (
                <tr key={idx} className="border-b border-border last:border-0">
                  <td className="px-3 py-1.5 text-foreground">{row?.name || "—"}</td>
                  <td className="px-3 py-1.5 text-muted-foreground">{row?.external_id || "—"}</td>
                  <td className="px-3 py-1.5 text-muted-foreground">
                    {row?.last_seen
                      ? new Date(row.last_seen).toLocaleDateString()
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Segment editor (modal content)                                     */
/* ------------------------------------------------------------------ */

const IndividualSegmentEditor = ({
  item,
  existingNames,
  workspaceId,
  onSave,
  onDelete,
  onCancel,
}: {
  item?: Segment | null;
  existingNames: string[];
  workspaceId: string;
  onSave: (segment: Segment) => void;
  onDelete: () => void;
  onCancel: () => void;
}) => {
  const [name, setName] = useState(item?.name || "");
  const [description, setDescription] = useState(item?.description || "");
  const [color, setColor] = useState(item?.color || "#3b82f6");
  const [nameError, setNameError] = useState("");

  const initialFilter = useMemo(() => {
    if (item?.fit_filter && (item.fit_filter as any).predicates) return item.fit_filter;
    return structuredClone(EMPTY_FILTER);
  }, [item?.fit_filter]);

  const [filterState, setFilterState] = useState<any>(initialFilter);

  const handleFilterAdd = useCallback(() => {
    setFilterState((prev: any) => ({
      ...prev,
      predicates: [
        ...(prev.predicates || []),
        { predicates: [{ field: "", operator: "", value: "", type: "", entity_type: "column" }] },
      ],
    }));
  }, []);

  const handlePredicateAdd = useCallback((filterId?: number) => {
    setFilterState((prev: any) => {
      const next = { ...prev, predicates: [...(prev.predicates || [])] };
      if (filterId !== undefined && next.predicates[filterId]) {
        next.predicates[filterId] = {
          ...next.predicates[filterId],
          predicates: [
            ...next.predicates[filterId].predicates,
            { field: "", operator: "", value: "", type: "", entity_type: "column" },
          ],
        };
      }
      return next;
    });
  }, []);

  const handlePredicateRemove = useCallback((currentKey: number, filterId?: number) => {
    setFilterState((prev: any) => {
      const next = { ...prev, predicates: [...(prev.predicates || [])] };
      if (filterId !== undefined && next.predicates[filterId]) {
        const filtered = next.predicates[filterId].predicates.filter(
          (_: any, idx: number) => idx !== currentKey,
        );
        if (filtered.length === 0) {
          next.predicates = next.predicates.filter((_: any, idx: number) => idx !== filterId);
        } else {
          next.predicates[filterId] = { ...next.predicates[filterId], predicates: filtered };
        }
      }
      return next;
    });
  }, []);

  const handlePredicateChange = useCallback(
    (field: any, operator: any, value: any, type: any, entityType: any, id: number, filterId: number) => {
      setFilterState((prev: any) => {
        const next = { ...prev, predicates: [...(prev.predicates || [])] };
        if (next.predicates[filterId]) {
          const preds = [...next.predicates[filterId].predicates];
          preds[id] = { field, operator, value, type, entity_type: entityType };
          next.predicates[filterId] = { ...next.predicates[filterId], predicates: preds };
        }
        return next;
      });
    },
    [],
  );

  const hasValidFilter = (filterState?.predicates || []).some(
    (group: any) => (group?.predicates || []).some((p: any) => p?.field?.trim() && p?.operator?.trim()),
  );
  const canSave = name.trim().length > 0 && hasValidFilter;

  const validateAndSave = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError("Segment name is required.");
      return;
    }
    const isDuplicate = existingNames.some(
      (n) => n.toLowerCase() === trimmedName.toLowerCase(),
    );
    if (isDuplicate) {
      setNameError("A segment with this name already exists.");
      return;
    }
    setNameError("");

    onSave({
      id: item?.id || v4(),
      name: trimmedName,
      description: description.trim() || null,
      color,
      entity_relation: "accounts",
      fit_filter: filterState,
      metadata: item?.metadata || {},
    });
  };

  return (
    <div className="space-y-5">
      {/* Name */}
      <div className="space-y-1.5">
        <Label>Segment Name</Label>
        <Input
          placeholder="e.g., Enterprise, Mid-Market, Healthcare"
          value={name}
          onChange={(e) => { setName(e.target.value); setNameError(""); }}
        />
        {nameError && <p className="text-xs text-destructive">{nameError}</p>}
      </div>

      {/* Description */}
      <div className="space-y-1.5">
        <Label>Description</Label>
        <textarea
          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          placeholder="Describe what defines this segment and how it should be used."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
        />
      </div>

      {/* Color */}
      <ColorField label="Segment Color" value={color} onChange={setColor} />

      {/* Filter builder */}
      <div className="space-y-2">
        <Label>Filter Conditions</Label>
        <p className="text-xs text-muted-foreground">
          Define which accounts belong to this segment. Conditions within a group are combined with AND. Groups are combined with OR.
        </p>
        <div className="rounded-md border border-border bg-muted/30 p-2">
          {(filterState?.predicates || []).map((filter: any, idx: number) => (
            <div key={`sf-${idx}`}>
              <SignalFilterContainer
                selectedFilters={filter.predicates}
                handleFilter={handlePredicateChange}
                handleRemove={handlePredicateRemove}
                handleAdd={handlePredicateAdd}
                filterId={idx}
                entityRelation="accounts"
              />
              {idx < (filterState?.predicates?.length || 0) - 1 && (
                <p className="text-xs text-muted-foreground text-center py-1">— OR —</p>
              )}
            </div>
          ))}
          <Button variant="ghost" size="sm" className="mt-2" onClick={handleFilterAdd}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add Condition Group
          </Button>
        </div>
      </div>

      {/* Preview */}
      <div className="space-y-2">
        <Label>Account Preview</Label>
        <SegmentPreview workspaceId={workspaceId} filter={filterState} />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <div>
          {item && (
            <Button variant="destructive" size="sm" onClick={onDelete}>
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              Delete
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" onClick={validateAndSave} disabled={!canSave}>
            {item ? "Update" : "Create"}
          </Button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main page                                                          */
/* ------------------------------------------------------------------ */

const SegmentsPage = () => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const workspaceId = workspace?.id || "";

  const { data: businessContext, isLoading: contextLoading } = useBusinessContext(workspaceId);
  const updateMutation = useUpdateBusinessContext(workspaceId);

  const [segmentsDraft, setSegmentsDraft] = useState<Segment[]>([]);
  const [dirty, setDirty] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSegment, setEditingSegment] = useState<Segment | null>(null);

  // Sync draft from server data
  useEffect(() => {
    if (businessContext?.segments) {
      setSegmentsDraft(businessContext.segments);
      setDirty(false);
    }
  }, [businessContext?.segments]);

  const openCreate = () => {
    setEditingSegment(null);
    setModalOpen(true);
  };

  const openEdit = (segment: Segment) => {
    setEditingSegment(segment);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingSegment(null);
  };

  const handleSaveSegment = useCallback(
    (segment: Segment) => {
      setSegmentsDraft((prev) => {
        const idx = prev.findIndex((s) => s.id === segment.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = segment;
          return next;
        }
        return [...prev, segment];
      });
      setDirty(true);
      closeModal();
    },
    [],
  );

  const handleDeleteSegment = useCallback(() => {
    if (!editingSegment?.id) return;
    setSegmentsDraft((prev) => prev.filter((s) => s.id !== editingSegment.id));
    setDirty(true);
    closeModal();
  }, [editingSegment]);

  const handleSaveAll = async () => {
    try {
      await updateMutation.mutateAsync({ segments: segmentsDraft });
      setDirty(false);
      notify({
        type: "success",
        heading: "Segments saved",
        messageList: ["Your segment definitions have been saved to business context."],
      });
    } catch (error: any) {
      const detail = typeof error?.response?.data?.detail === "string"
        ? error.response.data.detail
        : "Failed to save segments.";
      notify({ type: "failure", heading: "Save failed", messageList: [detail] });
    }
  };

  // Names for duplicate validation (excluding current editing segment)
  const existingNames = useMemo(
    () =>
      segmentsDraft
        .filter((s) => s.id !== editingSegment?.id)
        .map((s) => s.name || "")
        .filter(Boolean),
    [segmentsDraft, editingSegment?.id],
  );

  if (contextLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center rounded-xl border border-border bg-card">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // When editing/creating, show the inline editor instead of the grid
  if (modalOpen) {
    return (
      <div className="mx-auto max-w-5xl space-y-4 px-4 py-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={closeModal}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <h2 className="text-lg font-semibold text-foreground">
            {editingSegment ? `Edit ${editingSegment.name || "Segment"}` : "New Segment"}
          </h2>
        </div>

        <IndividualSegmentEditor
          item={editingSegment}
          existingNames={existingNames}
          workspaceId={workspaceId}
          onSave={handleSaveSegment}
          onDelete={handleDeleteSegment}
          onCancel={closeModal}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-4">
      {/* Header */}
      <div className="space-y-1">
        <h2 className="text-xl font-semibold text-foreground">Segments</h2>
        <p className="text-sm text-muted-foreground">
          Define named account segments using filter conditions. Segments can be linked to health
          models so Ora evaluates accounts within each segment using tailored criteria.
        </p>
      </div>

      {/* Save bar */}
      {dirty && (
        <div className="flex items-center gap-3">
          <Badge variant="statusPill" status="warning">
            Unsaved changes
          </Badge>
          <Button
            onClick={() => void handleSaveAll()}
            disabled={updateMutation.isLoading}
            variant="outline"
            size="sm"
          >
            {updateMutation.isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Save segments
              </>
            )}
          </Button>
        </div>
      )}

      {/* Segments grid */}
      {segmentsDraft.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {segmentsDraft.map((segment) => (
            <button
              key={segment.id}
              type="button"
              onClick={() => openEdit(segment)}
              className="group flex flex-col rounded-lg border border-border bg-card p-4 text-left transition-colors hover:border-foreground/20 hover:bg-muted/30"
              style={{ borderLeftWidth: 4, borderLeftColor: segment.color || "#3b82f6" }}
            >
              <div className="flex items-center gap-2">
                <Layers className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground truncate">
                  {segment.name || "Unnamed Segment"}
                </span>
              </div>
              {segment.description && (
                <p className="mt-1.5 text-xs text-muted-foreground line-clamp-2">
                  {segment.description}
                </p>
              )}
            </button>
          ))}

          {/* Add card */}
          <button
            type="button"
            onClick={openCreate}
            className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-card p-4 text-muted-foreground transition-colors hover:border-foreground/20 hover:text-foreground"
          >
            <Plus className="h-5 w-5" />
            <span className="text-xs font-medium">Add Segment</span>
          </button>
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">No segments defined</CardTitle>
            <CardDescription>
              Create segments to group accounts by industry, company size, plan tier, or any filter
              criteria. Segments can then be linked to health models for targeted scoring.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" size="sm" onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Create your first segment
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SegmentsPage;
